import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import NumberField from "../../components/Editing/NumberField";

type StateProps = {
  editedEnumeration: Api.EventEnumeration;
  storedEnumeration: Api.EventEnumeration;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi;
};
interface ErrorStatus {
  errorStatus: boolean;
  errorMsg: string;
}

const eventEnumFields = nameofFactory<Api.EventEnumeration>();

export type DoorEditEnumProps = StateProps & DispatchProps & OwnProps;

export class EditDoorEnumeration extends React.Component<DoorEditEnumProps> {
  render(): JSX.Element {
    const {
      editedEnumeration,
      storedEnumeration,
      activeUser,
      toggleModal,
      dataFieldName
    } = this.props;
    const isNew = !storedEnumeration;
    const permission = hasWriteAccess(
      activeUser,
      "Door"
    );
    
    const errorenumName = this.isFieldValid(eventEnumFields("enumName"));
    const errorenumValue = this.isFieldValid(eventEnumFields("enumValue"));
    
    if (storedEnumeration) {
      storedEnumeration.comment = storedEnumeration.comment ? storedEnumeration.comment : "";
    }

    return (
      <Editor
        itemName={"Enumeration"}
        originalObject={storedEnumeration}
        editedObject={editedEnumeration}
        hasWritePermission={permission}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update(dataFieldName as Api.OptionsGrud)}
        create={() => this.props.editActions.add(dataFieldName as Api.OptionsGrud)}
        remove={{ callback: toggleModal }}
      >
        <NumberField
          id="enumValue"
          label="Value"
          value={editedEnumeration.enumValue}
          isError={errorenumValue.errorStatus}
          errorMessage={errorenumValue.errorMsg}
          readonly={!isNew}
          onChange={enumValue =>
            this.props.editActions.setEdited({ ...editedEnumeration, enumValue: enumValue })
          }
        />
        <TextField
          id="enumName"
          label="Enumeration Name"
          value={editedEnumeration.enumName}
          isError={errorenumName.errorStatus}
          errorMessage={errorenumName.errorMsg}
          readonly={!permission}
          onChange={enumName =>
            this.props.editActions.setEdited({ ...editedEnumeration, enumName: enumName.trim() })
          }
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedEnumeration.comment}
          isError={!this.isFieldValid(eventEnumFields("comment"))}
          readonly={!permission}
          onChange={comment =>
            this.props.editActions.setEdited({ ...editedEnumeration, comment: comment.trim() })
          }
        />
      </Editor>
    );
  }

  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  private checkSpecialCharacters = (str: string): boolean =>
    !/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_]+$/.test(str);

  private isFieldValid = (field: string): ErrorStatus => {
    const { editedEnumeration, storedEnumeration, pdApi } = this.props;
    const isNew = !storedEnumeration;
    switch (field) {
      case eventEnumFields("enumName"): {
        const { enumName } = editedEnumeration;

        if (this.isEmptyString(enumName)) {
          return { errorStatus: true, errorMsg: "The enumeration name cannot be empty." };
        } else if (
          this.checkSpecialCharacters(enumName)
        ) {
          return {
            errorStatus: true,
            errorMsg: "The enumeration name must contain only alphabets numbers and underscores."
          };
        } else {
          return _.values(pdApi[this.props.dataFieldName]).find(
            enumeration =>
              (isNew || enumeration.enumValue !== editedEnumeration.enumValue) &&
              enumeration.enumName.trim().toLowerCase() ===
                editedEnumeration.enumName.trim().toLowerCase()
          )
            ? { errorStatus: true, errorMsg: "The enumeration name must be unique." }
            : { errorStatus: false, errorMsg: "" };
        }
      }

      case eventEnumFields("enumValue"):
        if (this.props.storedEnumeration) {
          return { errorStatus: false, errorMsg: "" };
        }
        return _.values(pdApi[this.props.dataFieldName]).find(
          enumeration => enumeration.enumValue === editedEnumeration.enumValue
        )
          ? { errorStatus: true, errorMsg: "The value must be unique." }
          : { errorStatus: false, errorMsg: "" };

      default:
        return { errorStatus: false, errorMsg: "" };
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedEnumeration } = this.props;
    const temp = _.keys(editedEnumeration);
    for (const key of temp) {
      if (this.isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEnumeration: state.editView.editedObject,
  storedEnumeration: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDoorEnumeration);

